// ./src/components/features/CreateEventWizard/steps/PoliciesStep.tsx
//
// "What must guests agree to?" — shown when the Policies & waivers feature is on (ROADMAP A2b #62 P6(e): the obvious
// B2B one). Attaching is the Compliance page's own `AttachPolicyDrawer` — pick from the account's policies, or write a
// new one in place (it is attached on save) — so the wizard can never attach differently from the page.
//
// ⚠⚠ A WAIVER IS ATTACHED HERE AND MADE UNWORKABLE ON ANOTHER STEP. Whether anyone can ever sign it is decided by the
//   custom questions (a date of birth, an adult's email) — asked on the step BEFORE this one. So the step reads the
//   event's questions and shows the same gap warnings the Questions page and the dashboard show (`ComplianceGapAlerts`,
//   one rule in `utils/complianceSetup.ts`), with every fix kept INSIDE the wizard: the date of birth opens the Questions
//   drawer already filled in, "Review questions" goes back to that step.
// ⚠ "Guests must accept" is the attachment's own `isRequired` — the same switch as the Compliance page's.
// ⚠ Under a `PoliciesProvider`, as on the Compliance page: the drawer's "Create a policy" saves through that context, and
//   without it Save does NOTHING — no request, no error, the drawer just stays open (review, 2026-09-30).
// ⚠ The gap warnings wait for the QUESTIONS read too, and say nothing if it failed: an unread list is not "no
//   date-of-birth question", and its "Add" button would make a second one.

import React, { useMemo, useState } from 'react';
import { Stack, Switch, Text } from '@mantine/core';
import { STATUSES } from '@sdflc/utils';
import { useTranslation } from 'react-i18next';

import { DEFAULT_PAGE_SIZE, FEATURE_CODES } from '@/config';
import { PoliciesProvider } from '@/contexts';
import { useCreateGate, useEventPolicies, useRsvpQuestions, useToast } from '@/hooks';
import { EventPolicy, EventPolicyService, QUESTION_ASKED_ON } from '@/utils/api';
import { complianceSetupGaps } from '@/utils/complianceSetup';
import { ConfirmModal } from '@/components/generic';
import { ComplianceGapAlerts } from '@/components/features/Questions/ComplianceGapAlerts';
import { AttachPolicyDrawer } from '@/pages/private/EventCompliancePage/AttachPolicyDrawer';
import { QuestionDrawer } from '@/pages/private/EventQuestionsPage/QuestionDrawer';
import { BIRTHDAY_QUESTION_PREFILL } from '@/pages/private/EventQuestionsPage/questionPrefill';
import { refusalReason } from '@/utils';

import { useEventWizard } from '../WizardContext';
import { WizardItemRow } from '../components/WizardItemRow';
import { WizardListSection } from '../components/WizardListSection';

const PoliciesStepBody: React.FC<{ eventId: string }> = ({ eventId }) => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const { saving, goToStep } = useEventWizard();

  const { eventPolicies, isLoaded, error, reload } = useEventPolicies(eventId);
  const { questions, isLoaded: questionsLoaded, error: questionsError, reload: reloadQuestions } = useRsvpQuestions(eventId, QUESTION_ASKED_ON.RSVP);
  // Attaching is `pro_registration` — EventPolicyCore refuses it on a plan without it. Only a NEW attachment is refused.
  const gate = useCreateGate(FEATURE_CODES.PRO_REGISTRATION, { eventId });

  const [attachOpen, setAttachOpen] = useState(false);
  const [birthdayOpen, setBirthdayOpen] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<EventPolicy | null>(null);
  const [removing, setRemoving] = useState(false);
  // Attachments whose switch is being saved — a Set, so one row finishing never unlocks another.
  const [togglingIds, setTogglingIds] = useState<ReadonlySet<string>>(new Set());

  const sorted = useMemo(
    () => [...eventPolicies].sort((a, b) => (a.orderNo ?? 0) - (b.orderNo ?? 0) || (a.createdAt ?? '').localeCompare(b.createdAt ?? '')),
    [eventPolicies],
  );
  const attachedPolicyIds = useMemo(() => new Set(eventPolicies.map((ep) => ep.policyId).filter((id): id is string => !!id)), [eventPolicies]);
  const maxOrderNo = useMemo(() => eventPolicies.reduce((max, ep) => Math.max(max, Number(ep.orderNo ?? 0)), 0), [eventPolicies]);
  // ⚠ ACTIVE attachments only — a turned-off policy never reaches a guest, so it cannot be owed a question.
  const gaps = useMemo(
    () => complianceSetupGaps({ links: eventPolicies.filter((ep) => ep.status === STATUSES.ACTIVE), questions }),
    [eventPolicies, questions],
  );

  const setRequired = async (ep: EventPolicy, isRequired: boolean) => {
    const id = ep.id;

    if (!id) {
      return;
    }

    setTogglingIds((prev) => new Set(prev).add(id));

    const res = await EventPolicyService.update(id, { isRequired });

    if (res.didSucceed()) {
      await reload();
    } else {
      toast.error(refusalReason(res));
    }

    // ⚠ Released only once the list shows the saved value — earlier, the switch comes back on its OLD position and a
    //   second tap looks like the first was ignored.
    setTogglingIds((prev) => {
      const next = new Set(prev);

      next.delete(id);

      return next;
    });
  };

  const confirmRemove = async () => {
    if (!pendingRemove?.id) {
      return;
    }

    setRemoving(true);

    const res = await EventPolicyService.remove(pendingRemove.id);

    setRemoving(false);

    if (!res.didSucceed()) {
      toast.error(refusalReason(res));

      return;
    }

    setPendingRemove(null);
    await reload();
  };

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {t('events:wizard.policies.intro', {
          defaultValue:
            'Add the terms, waivers and consents guests agree to when they reply or buy — a photo release, a liability waiver. Your policies are kept in your account, so you write each one once and reuse it.',
        })}
      </Text>

      {isLoaded && !error && questionsLoaded && !questionsError ? (
        <ComplianceGapAlerts
          eventId={eventId}
          gaps={gaps}
          onFixed={() => void reloadQuestions()}
          inWizard={{ onAddBirthday: () => setBirthdayOpen(true), onReviewQuestions: () => goToStep('questions') }}
        />
      ) : null}

      <WizardListSection
        title={t('events:wizard.policies.title', { defaultValue: 'Policies & waivers' })}
        addLabel={t('events:wizard.policies.add', { defaultValue: 'Add a policy' })}
        onAdd={() => gate.canCreate && setAttachOpen(true)}
        gate={gate}
        gateWhat={t('events:wizard.policies.another', { defaultValue: 'another policy' })}
        disabled={saving}
        isLoaded={isLoaded}
        readError={error}
        onRetry={() => void reload()}
        errorTitle={t('events:wizard.policies.loadFailed', { defaultValue: 'Could not load the policies' })}
        operation="eventPolicyList"
        emptyText={t('events:wizard.policies.none', { defaultValue: 'No policies yet.' })}
        isEmpty={sorted.length === 0}
      >
        {sorted.map((ep) => (
          <WizardItemRow
            key={ep.id}
            icon="Gavel"
            title={ep.policy?.name ?? ''}
            subtitle={
              ep.status !== STATUSES.ACTIVE
                ? t('events:wizard.policies.off', { defaultValue: 'Turned off — guests do not see it' })
                : (ep.policy?.label ?? null)
            }
            disabled={saving}
            onRemove={() => setPendingRemove(ep)}
          >
            <Switch
              mt="xs"
              size="md"
              label={t('events:wizard.policies.required', { defaultValue: 'Guests must accept it' })}
              checked={!!ep.isRequired}
              disabled={saving || togglingIds.has(ep.id as string)}
              onChange={(e) => {
                const next = e.currentTarget.checked;

                void setRequired(ep, next);
              }}
            />
          </WizardItemRow>
        ))}
      </WizardListSection>

      <AttachPolicyDrawer
        opened={attachOpen}
        onClose={() => setAttachOpen(false)}
        eventId={eventId}
        attachedPolicyIds={attachedPolicyIds}
        maxOrderNo={maxOrderNo}
        onAttached={() => void reload()}
      />

      <QuestionDrawer
        opened={birthdayOpen}
        onClose={() => setBirthdayOpen(false)}
        eventId={eventId}
        question={null}
        prefill={BIRTHDAY_QUESTION_PREFILL}
        askedOn={QUESTION_ASKED_ON.RSVP}
        // The drawer closes itself on a full save; on a HALF-failed one it stays open to say so (#126), so this must not close it.
        onSaved={() => {
          void reloadQuestions();
        }}
      />

      <ConfirmModal
        opened={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => void confirmRemove()}
        type="danger"
        confirmInProgress={removing}
        title={t('events:wizard.policies.removeTitle', { defaultValue: 'Remove this policy from the event?' })}
        message={t('events:wizard.policies.removeMessage', {
          defaultValue: '“{{name}}” stays in your account’s policies; this event just stops asking for it.',
          name: pendingRemove?.policy?.name ?? '',
        })}
        confirmLabel={t('common:actions.remove', { defaultValue: 'Remove' })}
      />
    </Stack>
  );
};

export const PoliciesStep: React.FC = () => {
  const { t } = useTranslation(['events']);
  const { createdEventId } = useEventWizard();

  if (!createdEventId) {
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.noEvent', { defaultValue: 'Your event is created at the end of the Guests step.' })}
      </Text>
    );
  }

  return (
    <PoliciesProvider pageSize={DEFAULT_PAGE_SIZE} autoLoad={false} eventId={createdEventId}>
      <PoliciesStepBody eventId={createdEventId} />
    </PoliciesProvider>
  );
};
